import { Home, Phone, Mail, Briefcase } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { AopChip, OnFbcChip } from './aopMeta';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const fmtArea = (v) => (v == null || v === '' ? '—' : Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
export const fmtRupees = (v) => (v == null || v === '' ? '—' : `₹${Number(v).toLocaleString('en-IN')}`);
export const fmtDop = (d) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(d || '');
  return m ? `${m[3]}-${MONTHS[Number(m[2]) - 1]}-${m[1]}` : '—';
};

// Column template shared by the header and every row so all values line up.
export function gridTemplate(cols) {
  return ['5.5rem', 'minmax(0,1fr)', cols.carpet_area && '6.5rem', cols.possession_date && '6.5rem',
    cols.contact && 'minmax(0,11rem)', cols.aop_status && '8.5rem', '4.5rem'].filter(Boolean).join(' ');
}

export function DirectoryHeader({ cols }) {
  return (
    <div className="hidden md:grid items-center gap-3 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground bg-muted/30 border-b"
      style={{ gridTemplateColumns: gridTemplate(cols) }} data-testid="directory-table-header">
      <span>Flat</span>
      <span>Allottee</span>
      {cols.carpet_area && <span className="text-right" data-testid="directory-header-area">Area (sq ft)</span>}
      {cols.possession_date && <span className="text-right" data-testid="directory-header-dop">DOP</span>}
      {cols.contact && <span>Contact</span>}
      {cols.aop_status && <span>AOP</span>}
      <span className="text-right">Status</span>
    </div>
  );
}

function NameCell({ r }) {
  return (
    <div className="min-w-0 flex items-center gap-1.5">
      <span className="truncate">{r.name}</span>
      {r.holder_position && <span className="text-[10px] rounded bg-muted px-1.5 py-0.5 text-muted-foreground shrink-0" data-testid={`holder-pos-${r.id}`}>{['', '1st holder', '2nd holder', '3rd holder', '4th holder'][r.holder_position]}</span>}
      {r.qualification && <Badge variant="outline" className="text-[10px] shrink-0 hidden lg:inline-flex"><Briefcase className="h-2.5 w-2.5 mr-1" />{r.qualification}</Badge>}
    </div>
  );
}

function ContactCell({ r }) {
  if (!r.phone && !r.email) return <span className="text-muted-foreground">—</span>;
  return (
    <div className="min-w-0 flex flex-col text-[11px] leading-tight">
      {r.phone && <a href={`tel:${r.phone}`} className="text-primary truncate" data-testid={`resident-phone-${r.id}`}><Phone className="h-3 w-3 inline mr-1" />{r.phone}</a>}
      {r.email && <a href={`mailto:${r.email}`} className="text-primary truncate" data-testid={`resident-email-${r.id}`}><Mail className="h-3 w-3 inline mr-1" />{r.email}</a>}
    </div>
  );
}

export function DirectoryRow({ r, cols, actions, statusDot }) {
  return (
    <div className="px-3 py-2 text-sm hover:bg-muted/30 transition-colors" data-testid={`resident-${r.id}`}>
      <div className="hidden md:grid items-center gap-3" style={{ gridTemplateColumns: gridTemplate(cols) }}>
        <span className="font-medium flex items-center gap-1.5"><Home className="h-3 w-3 text-muted-foreground" />{r.parsed.original}</span>
        <NameCell r={r} />
        {cols.carpet_area && <span className="text-right tabular-nums" data-testid={`resident-area-${r.id}`}>{fmtArea(r.carpet_area_sqft)}</span>}
        {cols.possession_date && <span className="text-right tabular-nums" data-testid={`resident-dop-${r.id}`}>{fmtDop(r.possession_date)}</span>}
        {cols.contact && <ContactCell r={r} />}
        {cols.aop_status && <span className="flex items-center gap-1 flex-wrap">{r.aop_status && <AopChip status={r.aop_status} testid={`resident-aop-${r.id}`} />}{r.on_fbc && <OnFbcChip testid={`resident-onfbc-${r.id}`} />}</span>}
        <span className="flex items-center justify-end gap-1">{actions}{statusDot}</span>
      </div>
      <div className="md:hidden space-y-1">
        <div className="flex items-center gap-2">
          <span className="font-medium shrink-0">{r.parsed.original}</span>
          <div className="flex-1 min-w-0"><NameCell r={r} /></div>
          {actions}{statusDot}
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground pl-0.5">
          {cols.carpet_area && <span data-testid={`resident-area-m-${r.id}`}>Area: <b className="text-foreground">{fmtArea(r.carpet_area_sqft)}</b>{r.carpet_area_sqft != null && r.carpet_area_sqft !== '' ? ' sq ft' : ''}</span>}
          {cols.possession_date && <span data-testid={`resident-dop-m-${r.id}`}>DOP: <b className="text-foreground">{fmtDop(r.possession_date)}</b></span>}
          {r.aop_status && <AopChip status={r.aop_status} />}
          {cols.aop_status && r.on_fbc && <OnFbcChip />}
          {cols.contact && (r.phone || r.email) && <ContactCell r={r} />}
        </div>
      </div>
    </div>
  );
}
